import Pusher from 'pusher-js';

import { request } from './api';

/**
 * The Soketi connection (HANDOFF §8).
 *
 * ## The socket is a delivery mechanism, never a source of truth
 *
 * §8.2's rule, and the reason this file is as small as it is. An event says
 * *something changed*; the client then asks the API what. Nothing here parses a
 * payload into state, because the moment it did there would be two shapes for
 * the same data and no way to notice them diverging.
 *
 * It also means a dropped event is a latency problem and nothing more. Every
 * realtime surface is backed by a query that refetches on its own, so a
 * reconnect resyncs by asking rather than by replaying.
 *
 * ## One connection, module-scoped
 *
 * Not a hook and not a store: a websocket per component that wants events is a
 * websocket per component, and React StrictMode double-invokes effects in
 * development, which would open two on every mount.
 */

let pusher: Pusher | null = null;
let subscribedUserId: number | null = null;

const key = import.meta.env.VITE_SOKETI_KEY as string | undefined;

/** Whether realtime is configured. Without a key it simply does not run. */
export function isRealtimeEnabled(): boolean {
  return typeof key === 'string' && key !== '';
}

/**
 * Whether events are actually arriving.
 *
 * ## Configured is not connected, and the difference cost a fortnight
 *
 * Every poll in the app relaxes when realtime is on - a room checks every
 * fifteen seconds instead of three, because the socket is expected to tell it
 * sooner. That trade is only sound while the socket works.
 *
 * Production shipped with the wrong app key. Soketi refused every browser with
 * `App key ... does not exist`, but a key was *configured*, so
 * `isRealtimeEnabled()` said yes, every poll relaxed to fifteen seconds, and
 * nothing ever arrived to make up the difference. A host pausing a track took
 * about ten seconds to reach a listener. The bad key was the fault; treating
 * "a key exists" as "the socket works" is what turned it into the slow version
 * of the product rather than a visible failure.
 *
 * So the fallback now follows the connection rather than the configuration. A
 * socket that is down, blocked by a corporate proxy, or still connecting is not
 * healthy, and the polls tighten until it is.
 */
export function isRealtimeHealthy(): boolean {
  return isRealtimeEnabled() && pusher?.connection.state === 'connected';
}

/** Called whenever the connection changes state, so pollers can re-decide. */
const watchers = new Set<() => void>();

/**
 * Hear about connection changes.
 *
 * Returns a teardown. Used by anything whose poll interval depends on whether
 * the socket is carrying its share - without it a client that dropped would
 * keep its relaxed interval until the next tick, which is the fifteen seconds
 * this exists to avoid.
 */
export function onRealtimeHealthChange(listener: () => void): () => void {
  watchers.add(listener);

  return () => {
    watchers.delete(listener);
  };
}

function client(): Pusher {
  if (pusher) return pusher;

  pusher = new Pusher(key!, {
    /*
     * Same-origin, over whatever port the page is on.
     *
     * §8 is explicit about proxying rather than exposing 6001: corporate and
     * mobile carrier firewalls block odd ports, and this way there is no second
     * TLS certificate. nginx strips the `/ws` prefix before Soketi sees it.
     */
    wsHost: window.location.hostname,
    wsPort: Number(window.location.port) || (window.location.protocol === 'https:' ? 443 : 80),
    wssPort: Number(window.location.port) || 443,
    forceTLS: window.location.protocol === 'https:',
    enabledTransports: ['ws', 'wss'],
    wsPath: '/ws',
    cluster: '',

    /*
     * Channel authorisation goes through our API, not through Pusher's default
     * fetch.
     *
     * The access token lives in memory and is refreshed on 401 by the API
     * client - so the auth call has to go through the same client, or a
     * subscription that happens to land during a token refresh fails
     * permanently rather than retrying.
     */
    authorizer: (channel) => ({
      authorize(socketId, callback) {
        request<{ auth: string; channel_data?: string }>('/broadcasting/auth', {
          method: 'POST',
          body: { socket_id: socketId, channel_name: channel.name },
        })
          .then((data) => callback(null, data))
          .catch((error: Error) => callback(error, null));
      },
    }),
  });

  /*
   * State changes are broadcast to the pollers rather than logged.
   *
   * `state_change` fires for every transition - connecting, connected,
   * unavailable, failed, disconnected - and only the pollers care which. They
   * ask `isRealtimeHealthy()` when told something moved.
   */
  pusher.connection.bind('state_change', () => {
    for (const listener of watchers) listener();
  });

  return pusher;
}

/**
 * Listen to this account's own channel.
 *
 * Returns a teardown. Calling it again for the same account is a no-op, so a
 * component that mounts twice does not open two subscriptions.
 */
export function subscribeToUser(userId: number, onEvent: (event: string) => void): () => void {
  if (!isRealtimeEnabled()) {
    return () => {};
  }

  const name = `private-user.${userId}`;

  // A different account means the last one's channel has to go, or somebody
  // signing in on a shared browser keeps receiving the previous user's events.
  if (subscribedUserId !== null && subscribedUserId !== userId) {
    client().unsubscribe(`private-user.${subscribedUserId}`);
  }

  subscribedUserId = userId;

  const channel = client().subscribe(name);

  /*
   * Bound to the event names this app sends, not to a wildcard.
   *
   * `bind_global` would also fire for `pusher:subscription_succeeded` and every
   * other protocol frame, and the handler would then need to know which of
   * those are ours.
   */
  const handler = () => onEvent('notification.created');
  // A mastering render or reference analysis settled; the page refetches.
  const mastered = () => onEvent('mastering.changed');
  channel.bind('notification.created', handler);
  channel.bind('mastering.changed', mastered);

  return () => {
    channel.unbind('notification.created', handler);
    channel.unbind('mastering.changed', mastered);
  };
}

/**
 * Listen to a jam. One event, `jam.changed`, and the client refetches - the
 * same rule as the conversation channel, for the same reason.
 */
export interface JamPlaybackEvent {
  item_id: number | null;
  playing: boolean;
  position_ms: number;
  /** Server milliseconds when `position_ms` was true. */
  at: number | null;
  server_time: number;
  /** Who set it, so a player can ignore its own echo. */
  by: number;
}

/** One line in a jam's chat. See the backend's `JamChat`. */
export interface JamMessage {
  id: number;
  body: string;
  at: string;
  by: {
    id: number;
    username: string;
    display_name: string;
    verified: boolean;
    avatar: string | null;
  } | null;
}

export function subscribeToJam(
  jamId: number,
  onChange: () => void,
  onPlayback?: (event: JamPlaybackEvent) => void,
  onSaid?: (event: { message: JamMessage }) => void,
): () => void {
  if (!isRealtimeEnabled()) {
    return () => {};
  }

  const name = `private-jam.${jamId}`;
  /*
   * Counted, because a jam has two subscribers on one channel now - the
   * watcher that follows the clock and the chat panel - and the first of
   * them to unmount used to unsubscribe the channel out from under the
   * other. React's development double-mount made that every time.
   */
  const channel = client().subscribe(name);

  jamSubscribers.set(name, (jamSubscribers.get(name) ?? 0) + 1);

  channel.bind('jam.changed', onChange);

  /*
   * The one event that carries state. Playback is time, and a player told
   * "go and ask" is a round trip behind the one that pressed pause - the
   * drift the correction exists to remove. The document is still the truth
   * for anybody who joins or reconnects.
   */
  const playback = (event: JamPlaybackEvent) => onPlayback?.(event);

  channel.bind('jam.playback', playback);

  // The chat, for the same reason: a line that arrives after a refetch is
  // a line somebody has already typed over.
  const said = (event: { message: JamMessage }) => onSaid?.(event);

  channel.bind('jam.said', said);

  return () => {
    channel.unbind('jam.changed', onChange);
    channel.unbind('jam.playback', playback);
    channel.unbind('jam.said', said);

    const left = (jamSubscribers.get(name) ?? 1) - 1;

    if (left > 0) {
      jamSubscribers.set(name, left);

      return;
    }

    jamSubscribers.delete(name);
    client().unsubscribe(name);
  };
}

/** How many callers hold each jam channel. See `subscribeToJam`. */
const jamSubscribers = new Map<string, number>();

/**
 * Listen to one conversation's channel (§8.1).
 *
 * `message.new` and `message.read` both mean the same thing to a client that
 * rebuilds from the API: something about this thread changed, ask again. They
 * are separate events on the wire because they are separate facts, and a future
 * read-receipt indicator will want to tell them apart.
 *
 * Subscribing is authorised server-side against the participants table - see
 * `BroadcastAuthAction`. A channel name is not proof of anything, so asking for
 * somebody else's conversation simply fails to sign.
 */
export function subscribeToConversation(
  conversationId: number,
  onEvent: (event: string) => void,
): () => void {
  if (!isRealtimeEnabled()) {
    return () => {};
  }

  const channel = client().subscribe(`private-conversation.${conversationId}`);

  const onNew = () => onEvent('message.new');
  const onRead = () => onEvent('message.read');

  channel.bind('message.new', onNew);
  channel.bind('message.read', onRead);

  return () => {
    channel.unbind('message.new', onNew);
    channel.unbind('message.read', onRead);
    /*
     * Unsubscribed, unlike the user channel.
     *
     * A person opens many threads in a session and keeps one account. Leaving
     * every visited conversation subscribed would accumulate channels for the
     * life of the page, each one waking the tab for a thread nobody is looking
     * at any more.
     */
    client().unsubscribe(`private-conversation.${conversationId}`);
  };
}

/**
 * Listen to one room's channel (§8.1, §6.8).
 *
 * ## Public, unlike every other channel here
 *
 * No `private-` or `presence-` prefix, so the client never calls the auth
 * endpoint for it. That is deliberate: a live room has to be listenable by
 * somebody who has not signed in, and in the Pusher protocol the prefix is the
 * literal switch that decides whether authentication happens.
 *
 * Nothing sensitive rides on it. The state event carries the playhead, which
 * `GET /rooms/{slug}` would return to the same anonymous visitor anyway.
 *
 * ## The payload is passed through
 *
 * Unlike the notification and conversation channels, which deliberately carry
 * nothing and make the client refetch. The playhead is the exception: pushing
 * it is the entire reason a room feels live, and making every listener round
 * trip at the moment a track changes would put the latency straight back.
 */
/**
 * How many callers are listening to each station channel.
 *
 * ## Why a count and not a boolean
 *
 * A room page subscribes to the same channel from several places at once - the
 * room's own query, the crowd, and the profile's queue widget when it is
 * waiting in that room. They mount and unmount independently, and React
 * double-invokes effects in development, so a teardown is not evidence that
 * nobody is left.
 *
 * Without this, the first teardown called `unsubscribe` on the whole channel
 * and every other subscriber kept a handler bound to a channel the client had
 * already left. It failed silently and intermittently: reactions did nothing,
 * and the suite that covers them flaked for weeks on what looked like timing.
 */
const stationListeners = new Map<string, number>();

export function subscribeToStation(
  stationId: number,
  onEvent: (event: string, payload?: unknown) => void,
): () => void {
  if (!isRealtimeEnabled()) {
    return () => {};
  }

  const name = `station.${stationId}`;
  const channel = client().subscribe(name);

  stationListeners.set(name, (stationListeners.get(name) ?? 0) + 1);

  /*
   * Bound explicitly, one at a time.
   *
   * Pusher can bind everything with `bind_global`, and this deliberately does
   * not: an event the client does not know about should do nothing rather than
   * reach a handler that guesses. The cost is that a new server-side event is
   * invisible until it is named here - which is exactly how `station.tip` came
   * to be pushed correctly, subscribed to correctly, and silently ignored.
   */
  const events = [
    'station.state',
    'station.queue',
    'station.chat',
    'station.broadcast',
    'station.tip',
    'station.react',
    'station.crowd',
    /*
     * And the applause meter, which this list has now caught twice.
     *
     * It was pushed correctly, subscribed to correctly, and dropped here -
     * word for word what the note above says happened to `station.tip`. The
     * symptom is worse than it sounds: the tapper's own number climbs from
     * their optimistic count, so the feature looks like it works to the only
     * person who cannot tell that it does not. Everybody else's meter sat
     * still, and a room hit a thousand for an audience of one.
     */
    'station.hype',
  ];
  const handlers = events.map((event) => {
    const handler = (payload?: unknown) => onEvent(event, payload);
    channel.bind(event, handler);

    return { event, handler };
  });

  let released = false;

  return () => {
    // A caller that tears down twice must not take somebody else's turn off
    // the count with it.
    if (released) return;

    released = true;

    for (const { event, handler } of handlers) channel.unbind(event, handler);

    const left = (stationListeners.get(name) ?? 1) - 1;

    if (left > 0) {
      stationListeners.set(name, left);

      return;
    }

    stationListeners.delete(name);

    /*
     * Unsubscribed once the last one leaves, like a conversation and unlike the
     * user channel. Somebody dips into several rooms in an evening, and each
     * one left subscribed keeps waking the tab for a broadcast nobody is
     * listening to.
     */
    client().unsubscribe(name);
  };
}

/**
 * Drop the connection entirely, on sign-out.
 *
 * Not just unsubscribe: the socket carries an identity through its auth
 * signatures, and leaving it open across a sign-out means the next account
 * inherits a connection authorised for the last one.
 */
export function disconnectRealtime(): void {
  pusher?.disconnect();
  pusher = null;
  subscribedUserId = null;
}
